import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { updateUserSetting } from "./utils/firestoreService";
import { appName } from "./globals";
import { useAuth } from "./utils/authContext";
import { useFeatureFlags } from "./contexts/FeatureFlagContext";
import { SETTINGS_GROUPS } from "./config/featureFlagDefinitions";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { CircleQuestionMark } from "lucide-react";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { auth, db } from "./utils/firebase";

const Settings = () => {
  const { currentUser } = useAuth();
  // A different account must never inherit selections or pending requests.
  return <UserSettings key={currentUser?.uid || 'signed-out'} userId={currentUser?.uid} />;
};

const UserSettings = ({ userId }) => {
  const navigate = useNavigate();
  const { flags, visibleFlags = [], isLoading: flagsLoading } = useFeatureFlags();
  const [settings, setSettings] = useState({});
  const [saveStates, setSaveStates] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const session = useRef(null);

  useEffect(() => {
    // Gate on userSettingsMenu flag once flags have resolved
    if (!flagsLoading && !flags?.userSettingsMenu) {
      navigate("/dashboard");
      return;
    }
  }, [flagsLoading, flags?.userSettingsMenu, navigate]);

  useEffect(() => {
    const scope = { active: true, confirmed: {}, pending: new Set() };
    session.current = scope;
    setIsLoading(true);
    setSettings({});
    setSaveStates({});
    let unsubscribe;
    const receiveUserDoc = userDoc => {
      if (!scope.active) return;
      if (userDoc.exists()) {
        scope.confirmed = { ...userDoc.data().settings };
        setSettings(scope.confirmed);
        setIsLoading(false);
      } else {
        console.warn("User document not found, redirecting to dashboard");
        navigate("/dashboard");
      }
    };
    const handleLoadError = error => {
      if (!scope.active) return;
      console.error("Error fetching user data:", error);
      navigate("/dashboard");
    };
    const fetchUserData = async () => {
      if (userId) {
        try {
          const userRef = doc(db, "users", userId);
          const userDoc = await getDoc(userRef);
          if (!scope.active) return;
          if (userDoc.metadata?.hasPendingWrites) {
            // Reopening during a write must not adopt the SDK's optimistic
            // local value as confirmed. Wait for acknowledgement or rollback.
            unsubscribe = onSnapshot(userRef, { includeMetadataChanges: true }, snapshot => {
              if (snapshot.metadata.hasPendingWrites) return;
              unsubscribe?.();
              receiveUserDoc(snapshot);
            }, handleLoadError);
          } else {
            receiveUserDoc(userDoc);
          }
        } catch (error) {
          handleLoadError(error);
        }
      }
    };

    fetchUserData();
    return () => { scope.active = false; unsubscribe?.(); };
  }, [userId, navigate]);

  const handleSettingChange = async (field, value) => {
    const scope = session.current;
    if (!scope?.active || isLoading || scope.pending.has(field)) return;
    // The ref guards rapid duplicate events before React disables the radios.
    scope.pending.add(field);
    setSettings(current => ({ ...current, [field]: value }));
    setSaveStates(current => ({ ...current, [field]: { status: 'saving', value } }));

    try {
      await updateUserSetting(userId, field, value);
      // A same-account save still refreshes menus if the modal was closed while
      // it was pending. Never refresh another account from a late completion.
      if (field === 'betaDocumentTypes' && auth.currentUser?.uid === userId) {
        window.dispatchEvent(new Event('documentTypesUpdated'));
      }
      if (!scope.active) return;
      scope.confirmed = { ...scope.confirmed, [field]: value };
      setSaveStates(current => ({ ...current, [field]: null }));
    } catch (error) {
      if (!scope.active) return;
      console.error("Error updating user setting:", error);
      setSettings(current => ({ ...current, [field]: scope.confirmed[field] }));
      setSaveStates(current => ({ ...current, [field]: { status: 'error', value } }));
    } finally {
      scope.pending.delete(field);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="header-container">
          <Tooltip title="Go back" arrow>
            <button className="back-button" onClick={handleBack}>
              &larr;
            </button>
          </Tooltip>
          <div className="page-title">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="header-container">
        <Tooltip title="Go back" arrow>
          <button className="back-button" onClick={handleBack}>
            &larr;
          </button>
        </Tooltip>
        <div className="page-title">Your {appName} Settings</div>
      </div>

      {SETTINGS_GROUPS.map((group) => {
        const groupFlags = visibleFlags
          .filter((f) => f.settingsGroup === group.key)
          .sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999));
        if (groupFlags.length === 0) return null;
        return (
          <div className="settings-section" key={group.key}>
            <h3 className="settings-section-title">{group.label}</h3>
            <div className="settings-grid">
              {groupFlags.map((flag) => {
                const saveState = saveStates[flag.userSettingKey];
                const saving = saveState?.status === 'saving';
                const failed = saveState?.status === 'error';
                const feedbackId = `setting-feedback-${flag.key}`;
                return (
                <div className="settings-item" key={flag.key} role="group" aria-labelledby={`setting-label-${flag.key}`}>
                  <div className="settings-label">
                    <label id={`setting-label-${flag.key}`}>{flag.userLabel}:</label>
                    {flag.showBetaBadge && (
                      <Tooltip title="Beta Feature" arrow>
                        <span className="beta-badge">BETA</span>
                      </Tooltip>
                    )}
                  </div>
                  <div className="settings-help-icon">
                    <Tooltip title={flag.userTooltip}>
                      <IconButton>
                        <CircleQuestionMark size={20} />
                      </IconButton>
                    </Tooltip>
                  </div>
                  <div className="settings-choices">
                    <label>
                      <input
                        type="radio"
                        name={flag.userSettingKey}
                        value="yes"
                        disabled={saving}
                        aria-describedby={saveState ? feedbackId : undefined}
                        checked={settings[flag.userSettingKey] === true}
                        onChange={() => handleSettingChange(flag.userSettingKey, true)}
                      />
                      Yes
                    </label>
                    <label>
                      <input
                        type="radio"
                        name={flag.userSettingKey}
                        value="no"
                        disabled={saving}
                        aria-describedby={saveState ? feedbackId : undefined}
                        checked={settings[flag.userSettingKey] === false || settings[flag.userSettingKey] === undefined}
                        onChange={() => handleSettingChange(flag.userSettingKey, false)}
                      />
                      No
                    </label>
                  </div>
                  {saving && (
                    <div id={feedbackId} className="settings-save-feedback" role="status">Saving…</div>
                  )}
                  {failed && (
                    <div className="settings-save-feedback settings-save-error">
                      <span id={feedbackId} role="alert">Couldn’t save this setting.</span>
                      <button type="button" className="settings-retry" onClick={() => handleSettingChange(flag.userSettingKey, saveState.value)}>
                        Try again
                      </button>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Settings;
